import React, { useState } from 'react';
import Navbar from '../../components/navbar/navbar';
import Footer from '../../components/footer/index';
import { useDarkMode } from '../../components/darkmode/DarkModeContext';
import Diskon from '../../components/layanan/fiber_optic/paket';
import { Wifi, Cpu, Server, Zap, Shield, Users, CheckCircle, Clock, MessageCircle, ArrowRight, Cable, Network, Cctv } from 'lucide-react';

const FiberOpticLanPage = () => {
  const { isDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState('fiber');

  const services = {
    fiber: [
      {
        icon: <Cable className="w-8 h-8" />,
        title: "Instalasi Fiber Optic",
        description: "Pemasangan kabel fiber optik untuk kecepatan dan bandwidth tinggi",
        benefits: ["Kecepatan hingga 10Gbps", "Jangkauan hingga 80km", "Tahan interferensi elektromagnetik"]
      },
      {
        icon: <Zap className="w-8 h-8" />,
        title: "Fusion Splicing",
        description: "Penyambungan kabel fiber optik dengan teknik fusion untuk loss minimal",
        benefits: ["Loss < 0.1dB", "Koneksi stabil", "Durable connection"]
      },
      {
        icon: <Network className="w-8 h-8" />,
        title: "OTDR Testing",
        description: "Pengujian kualitas jaringan fiber optik dengan Optical Time Domain Reflectometer",
        benefits: ["Fault detection", "Performance analysis", "Documentation report"]
      }
    ],
    lan: [
      {
        icon: <Cpu className="w-8 h-8" />,
        title: "Structured Cabling",
        description: "Instalasi kabel terstruktur untuk jaringan LAN yang rapi dan terorganisir",
        benefits: ["Tidy installation", "Easy maintenance", "Scalable infrastructure"]
      },
      {
        icon: <Server className="w-8 h-8" />,
        title: "Network Rack Installation",
        description: "Pemasangan server rack dan perangkat jaringan profesional",
        benefits: ["Professional setup", "Proper ventilation", "Cable management"]
      },
      {
        icon: <Wifi className="w-8 h-8" />,
        title: "Wireless Network",
        description: "Setup jaringan nirkabel dengan coverage optimal dan keamanan tinggi",
        benefits: ["Seamless roaming", "Multiple SSID", "Guest network"]
      }
    ]
  };

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "High Speed Connectivity",
      description: "Kecepatan transfer data hingga 10Gbps untuk kebutuhan bandwidth tinggi",
      color: "bg-blue-500"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Reliable & Stable",
      description: "Jaringan yang stabil dan handal untuk operasional 24/7",
      color: "bg-green-500"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Scalable Infrastructure",
      description: "Desain jaringan yang mudah dikembangkan sesuai pertumbuhan bisnis",
      color: "bg-purple-500"
    },
    {
      icon: <Cctv className="w-8 h-8" />,
      title: "Professional Installation",
      description: "Instalasi oleh tim ahli berpengalaman dengan standar industri",
      color: "bg-yellow-500"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "24/7 Support",
      description: "Dukungan teknis penuh selama 24 jam untuk maintenance dan troubleshooting",
      color: "bg-red-500"
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Quality Guarantee",
      description: "Garansi kualitas pekerjaan dan material yang digunakan",
      color: "bg-indigo-500"
    }
  ];

  const useCases = [
    {
      title: "Enterprise Network",
      description: "Jaringan korporat dengan multiple department dan high availability",
      image: "/image/layanan/enterprise-network.png",
      benefits: ["Multi-floor coverage", "High availability", "Centralized management"]
    },
    {
      title: "Data Center",
      description: "Infrastructure untuk data center dengan redundancy dan scalability",
      image: "/image/layanan/data-center.png",
      benefits: ["Fiber backbone", "Redundant links", "Cooling system"]
    },
    {
      title: "Smart Building",
      description: "Jaringan terintegrasi untuk building automation system",
      image: "/image/layanan/smart-building.png",
      benefits: ["IoT integration", "Energy management", "Security systems"]
    },
    {
      title: "Campus Network",
      description: "Jaringan untuk kampus dan pendidikan dengan wide coverage",
      image: "/image/layanan/campus-network.png",
      benefits: ["Wide area coverage", "Multiple access points", "Content filtering"]
    }
  ];


  const contactViaWhatsApp = (packageName = "Fiber Optic & LAN Installation") => {
    const phoneNumber = "6287778882022";
    const message = `Halo, saya tertarik dengan layanan ${packageName}. Bisakah saya mendapatkan informasi lebih lanjut?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      <Navbar />

      {/* Hero Section */}
      <section
        className={`py-20 px-4 relative overflow-hidden ${isDarkMode ? 'bg-gray-900' : ''}`}
        style={{
          backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
          backgroundImage: isDarkMode
            ? "linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('/image/fiber-lan-dark.jpg')"
            : "linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.8)), url('/image/fiber-lan-hero.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto relative z-10 text-center">
          <div className={`inline-flex items-center gap-2 ${isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-[#1E9B4C]'} px-4 py-2 rounded-full text-sm font-medium mb-6`}>
            <Cable className="w-4 h-4" />
            Network Infrastructure Solutions
          </div>

          <h1 className={`text-4xl md:text-6xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Instalasi Fiber Optic &<br />
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1E9B4C] to-green-700">
              Local Area Network (LAN)
            </span>
          </h1>

          <p className={`text-xl max-w-3xl mx-auto mb-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Layanan instalasi kabel fiber optik dan jaringan lokal (LAN) untuk memastikan kecepatan dan stabilitas koneksi data, mendukung kebutuhan transfer data besar dan komunikasi real-time.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => contactViaWhatsApp("Fiber Optic & LAN Installation")}
              className="bg-[#1E9B4C] hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Konsultasi Gratis
            </button>
          </div>
        </div>
      </section>

       <div className="absolute inset-0">
        <img
          src="https://telnet.net.id/assets/bg.png"
          alt="Background pattern"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent dark:via-black/40"></div>
      </div>

      {/* Features Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Keunggulan Layanan Kami
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Solusi jaringan terintegrasi untuk performa optimal dan keandalan maksimal
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className={`group p-6 rounded-xl shadow-lg overflow-hidden transform hover:scale-105 hover:shadow-2xl transition-all duration-300 ${isDarkMode ? 'bg-gray-800 border border-gray-700 hover:border-[#1E9B4C] shadow-gray-900/50' : 'bg-white border border-gray-100 hover:border-[#1E9B4C] shadow-gray-300/20'}`}>
                <div className={`w-16 h-16 rounded-lg flex items-center justify-center text-white mb-4 ${feature.color || 'bg-[#1E9B4C]'}`}>
                  {feature.icon}
                </div>
                <h3 className={`text-xl font-bold mb-3 transition-colors ${isDarkMode ? 'text-white group-hover:text-[#1E9B4C]' : 'text-gray-900 group-hover:text-[#1E9B4C]'}`}>
                  {feature.title}
                </h3>
                <p className={`transition-colors ${isDarkMode ? 'text-gray-300 group-hover:text-gray-200' : 'text-gray-600 group-hover:text-gray-700'}`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Layanan Kami
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Pilihan layanan lengkap untuk semua kebutuhan jaringan Anda
            </p>
          </div>

          <div className="flex justify-center mb-8">
            <div className={`inline-flex rounded-lg p-1 shadow-lg ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
              {Object.keys(services).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${activeTab === tab ? 'bg-[#1E9B4C] text-white shadow-lg' : isDarkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
                >
                  {tab === 'fiber' ? 'Fiber Optic' : 'LAN Installation'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services[activeTab].map((service, index) => (
              <div key={index} className={`p-6 rounded-xl border-2 transition-all duration-300 hover:border-[#1E9B4C] hover:shadow-lg transform hover:scale-105 ${isDarkMode ? 'bg-gray-800 border-gray-700 shadow-gray-900/50 hover:shadow-xl hover:bg-gray-750' : 'bg-white border border-gray-200 shadow-gray-300/20 hover:shadow-xl hover:bg-gray-50'}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#1E9B4C] flex items-center justify-center text-white">
                    {service.icon}
                  </div>
                  <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {service.title}
                  </h3>
                </div>
                <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {service.description}
                </p>
                <div className="space-y-2">
                  {service.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Berbagai Aplikasi Penggunaan
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Solusi jaringan untuk berbagai skala dan kebutuhan bisnis
            </p>
          </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 shadow-gray-300/20'
                }`}
              >
                <div className="relative">
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className={`absolute inset-0 ${
                    isDarkMode 
                      ? 'bg-gradient-to-t from-gray-800/20 to-transparent' 
                      : 'bg-gradient-to-t from-white/20 to-transparent'
                  }`}></div>
                </div>
                <div className="p-6">
                  <h3
                    className={`text-xl font-bold mb-3 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {useCase.title}
                  </h3>
                  <p
                    className={`mb-4 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  >
                    {useCase.description}
                  </p>
                  <div className="space-y-2">
                    {useCase.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      

      {/* Pricing Section */}
      <Diskon />

      {/* CTA Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${isDarkMode ? 'bg-gray-800' : 'bg-[#1E9B4C]'}`}>
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Siap Tingkatkan Infrastruktur Jaringan Anda?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Dapatkan konsultasi gratis dari ahli jaringan kami untuk solusi Fiber Optic dan LAN yang tepat untuk kebutuhan bisnis Anda.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => contactViaWhatsApp("Fiber Optic & LAN Installation")}
              className="bg-white text-[#1E9B4C] hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <MessageCircle className="w-5 h-5" />
              Konsultasi Gratis
            </button>
            <button 
              onClick={() => contactViaWhatsApp("Enterprise Network Solution")}
              className="border-2 border-white text-white hover:bg-white hover:text-[#1E9B4C] font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <ArrowRight className="w-5 h-5" />
              Hubungi Sales
            </button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default FiberOpticLanPage;